import React, { useState, useEffect } from 'react';
import { pageTabsSx } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import {
  Box, Paper, Tabs, Tab,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Schedule as ScheduleIcon,
  Repeat as RepeatIcon,
  NotificationsActive as AlertsIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import OverviewTab from './communications/OverviewTab';
import SchedulesTab from './communications/SchedulesTab';
import RoundupsTab from './communications/RoundupsTab';
import AlertsTab from './communications/AlertsTab';
import TemplatesTab from './communications/TemplatesTab';

const TAB_IDS = ['overview', 'schedules', 'roundups', 'alerts', 'templates'] as const;
type TabId = typeof TAB_IDS[number];

const TABS: { id: TabId; label: string; icon: React.ReactElement }[] = [
  { id: 'overview',   label: 'Overview',   icon: <DashboardIcon /> },
  { id: 'schedules',  label: 'Schedules',  icon: <ScheduleIcon /> },
  { id: 'roundups',   label: 'Round-Ups',  icon: <RepeatIcon /> },
  { id: 'alerts',     label: 'Alerts',     icon: <AlertsIcon /> },
  { id: 'templates',  label: 'Templates',  icon: <EmailIcon /> },
];

const tabIndexFromParams = (params: URLSearchParams): number => {
  const t = params.get('tab') as TabId;
  const idx = TAB_IDS.indexOf(t);
  return idx >= 0 ? idx : 0;
};

const Communications: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    tabIndexFromParams(new URLSearchParams(window.location.search))
  );

  // Sidebar / deep links update the URL — mirror into local tab state only (no write-back)
  useEffect(() => {
    const idx = tabIndexFromParams(searchParams);
    setActiveTab((prev) => (prev !== idx ? idx : prev));
  }, [searchParams]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSearchParams({ tab: TAB_IDS[newValue] }, { replace: true });
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader title="Communications" subtitle="Insights" />

      <Paper sx={{ mb: 3, borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={pageTabsSx}
        >
          {TABS.map((t) => (
            <Tab
              key={t.id}
              label={isMobile ? undefined : t.label}
              icon={t.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      <Box>
        {activeTab === 0 && (
          <OverviewTab
            onNavigate={(tab) => {
              const idx = TAB_IDS.indexOf(tab as TabId);
              if (idx >= 0) handleTabChange({} as React.SyntheticEvent, idx);
            }}
          />
        )}
        {activeTab === 1 && <SchedulesTab />}
        {activeTab === 2 && <RoundupsTab />}
        {activeTab === 3 && <AlertsTab />}
        {activeTab === 4 && <TemplatesTab />}
      </Box>
    </Box>
  );
};

export default Communications;
